import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { PolicyReassignForm } from '@time-off-v3/forms';
import { PolicyDetailsTabsMenuService } from '@app/modules/time-off-v3/services/menus/policy-details-menu.service';
import { AnalyticService, NotifyService } from '@app/services';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { TranslatableKey } from '@app/types/translatable.type';
import { TimeOffRequestRules } from '@models/time-off-v3/time-off-request-rules.model';
import { TimeOffService } from '@app/modules/time-off-v3/services';

@Component({
    templateUrl: './policy-show.template.html',
    styleUrls: ['./policy-show.style.scss'],
})
export class PolicyShowView implements OnInit {
    @ViewChild('deletePolicyModal') deletePolicyModal: DeprecatedConfirmationDialogComponent;
    @ViewChild('policyReassignForm') policyReassignForm: PolicyReassignForm;
    @ViewChild('policyReassignDialog') policyReassignDialog: DeprecatedModalFormComponent;

    canReassign: boolean = false;
    isLoading = false;
    timeOffPolicy: TimeOffPolicy = new TimeOffPolicy();
    timeOffTypes: TimeOffType[] = [];
    timeOffRequestRule: TimeOffRequestRules;
    unitOfTime: TranslatableKey;

    constructor(
        private analyticService: AnalyticService,
        public menuService: PolicyDetailsTabsMenuService,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private timeOffService: TimeOffService
    ) {}

    ngOnInit(): void {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyViewTimeOffPolicy);

        this.timeOffPolicy = this.route.snapshot.data.models.get('policy');
        this.timeOffRequestRule = this.timeOffPolicy.timeOffRequestRule;
        this.unitOfTime = this.timeOffPolicy.timeOffType.unitOfTimePluralTranslation as TranslatableKey;

        this.loadData();
    }

    async loadData(): Promise<void> {
        try {
            const [timeOffTypes] = await TimeOffType.with('timeOffPolicies').all();
            this.timeOffTypes = timeOffTypes;

            const targetType = this.timeOffTypes.find(
                (timeOffType) => timeOffType.id === this.timeOffPolicy.timeOffTypeId
            );
            this.canReassign = targetType.timeOffPolicies.length > 1;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        } finally {
            this.isLoading = false;
        }
    }

    async onCopyTimeOffPolicy(): Promise<void> {
        try {
            this.isLoading = true;
            await this.timeOffService.createCopy(this.timeOffPolicy.id);
            this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies']);
            this.notify.success('time-off-v3.timeOffPolicyHasBeenCopied');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    async onDeletePolicy(): Promise<void> {
        try {
            const approved = await this.deletePolicyModal.show();

            if (!approved) {
                return;
            }

            await this.timeOffPolicy.delete();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyDeleteTimeOffPolicy);

            this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies']);
            this.notify.success('time-off-v3.timeOffPolicyHasBeenDeleted');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onReassignTimeOffPolicy(): void {
        const targetType = this.timeOffTypes.find((timeOffType) => timeOffType.id === this.timeOffPolicy.timeOffTypeId);
        this.policyReassignForm.sourcePolicy = this.timeOffPolicy;
        this.policyReassignForm.policies = targetType.timeOffPolicies.filter((p) => p.id !== this.timeOffPolicy.id);
        this.policyReassignDialog.show();
    }

    async onReassignPolicy(): Promise<void> {
        await this.timeOffService.reAssignEmployeesToPolicy(
            this.policyReassignForm.sourcePolicy.id,
            this.policyReassignForm.selectPolicy,
            {
                fromHireDate: !this.policyReassignForm.isCustomDate,
                effectiveAt: this.policyReassignForm.effectiveAt,
            }
        );
        this.notify.success('time-off-v3.employeesSuccessfullyAssignedToPolicy');
        this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies']);
    }
}
